import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { orderService } from '../services/order.service';
import { OrderResponse } from '../types/order.types';
import { OrderDetailsModal } from '../pages/Orders/OrderDetailsModal';
import { useNotification } from './NotificationContext';
import { useLanguage } from './LanguageContext';

interface OrderModalContextValue {
  openOrderModal: (orderId: number) => void;
  closeOrderModal: () => void;
  isOpen: boolean;
  order: OrderResponse | null;
}

const OrderModalContext = createContext<OrderModalContextValue | undefined>(undefined);

interface OrderModalProviderProps {
  children: ReactNode;
}

export const OrderModalProvider: React.FC<OrderModalProviderProps> = ({ children }) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { error: showError } = useNotification();
  const { t } = useLanguage();

  // Функция для открытия модального окна
  const openOrderModal = useCallback((orderId: number) => {
    const params = new URLSearchParams(location.search);
    params.set('orderId', orderId.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // Функция для закрытия модального окна
  const closeOrderModal = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('orderId');
    const newSearch = params.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    setIsOpen(false);
    setOrder(null);
  }, [location.pathname, location.search, navigate]);

  // Следим за изменениями URL параметра orderId
  useEffect(() => {
    const orderIdParam = searchParams.get('orderId');

    if (orderIdParam) {
      const orderId = parseInt(orderIdParam, 10);

      if (!isNaN(orderId) && orderId > 0) {
        // Загружаем заказ, если он не загружен или изменился ID
        if (!order || order.id !== orderId) {
          setLoading(true);
          orderService.getOrder(orderId)
            .then((data) => {
              setOrder(data);
              setIsOpen(true);
            })
            .catch((err) => {
              console.error('Failed to load order:', err);
              showError(t('orders.errorLoading'));
              closeOrderModal();
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          setIsOpen(true);
        }
      } else {
        // Некорректный ID - закрываем модалку
        closeOrderModal();
      }
    } else {
      // Нет параметра в URL - закрываем модалку
      if (isOpen) {
        setIsOpen(false);
        setOrder(null);
      }
    }
  }, [searchParams, order, isOpen, closeOrderModal, showError, t]);

  return (
    <OrderModalContext.Provider
      value={{
        openOrderModal,
        closeOrderModal,
        isOpen,
        order,
      }}
    >
      {children}

      {/* Глобальное модальное окно */}
      {order && (
        <OrderDetailsModal
          isOpen={isOpen}
          onClose={closeOrderModal}
          order={order}
        />
      )}
    </OrderModalContext.Provider>
  );
};

export const useOrderModal = (): OrderModalContextValue => {
  const context = useContext(OrderModalContext);
  if (!context) {
    throw new Error('useOrderModal must be used within OrderModalProvider');
  }
  return context;
};
